import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import { Box, Flex, Heading, Image, Text, TextProps } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import useTheme from '@rover/kibble/themes/useTheme';

type SectionProps = React.ComponentProps<typeof Section>;

// subset of valid theme colors
type QuickFactsColorOption = 'green' | 'yellow' | 'pink' | 'blue';

type Props = {
  themeColor?: QuickFactsColorOption;
  stickerImageSrc?: string;
  /** Must provide a sticker image if using a sticker alignment */
  stickerAlignment?: 'left' | 'right';
  subtitle?: React.ReactNode;
};

const ThemeColorContext = React.createContext('green');

// These sizes are static. The difference is 40px, which is the amount the sticker will overlap the top of the box. That's well within our y-margin of 16x (64px), so we know the sticker can't overlap any adjacent elements.
const CONTAINER_MIN_HEIGHT = '120px';
const STICKER_HEIGHT = '160px';

// styled component is necessary in order to add vertical-align to a Kibble Image
// vertical-align is necessary to remove a small space from below the image
const StickerImage = styled(Image)`
  vertical-align: bottom;
`;

const QuickFactSticker: FC<Pick<Props, 'stickerImageSrc'>> = ({ stickerImageSrc }) => {
  if (!stickerImageSrc) {
    return null;
  }
  return (
    <Box display={['none', 'none', 'block']} flex="1 1 33%" position="relative" alignSelf="stretch">
      <Box position="absolute" bottom="0" left="50%" sx={{ transform: 'translateX(-50%)' }}>
        <StickerImage
          src={stickerImageSrc}
          description=""
          width="auto"
          height={STICKER_HEIGHT}
          resizeMethod="contain"
        />
      </Box>
    </Box>
  );
};

const QuickFact: FC<SectionProps & Props> = ({
  children,
  themeColor = 'green',
  stickerImageSrc,
  stickerAlignment = 'right',
  subtitle,
  ...sectionProps
}) => {
  const { colors } = useTheme();

  return (
    <ThemeColorContext.Provider value={themeColor}>
      <Section
        // setting a large y margin makes spacing more consistent due to margin collapsing. Smaller margins of adjacent components will be collapsed (margin collapse favors the larger margin), letting this component take care of the full space between components when it is visible.

        // This should help us avoid having to set custom margins on each fact to accommodate inconsistent spacing of pre-existing components.
        my="16x"
        backgroundColor={colors[themeColor]['100']}
        sx={{
          overflow: 'visible',
        }}
        {...sectionProps}
      >
        <Flex gap="4x" alignItems="center" minHeight={CONTAINER_MIN_HEIGHT}>
          {stickerAlignment === 'left' && <QuickFactSticker stickerImageSrc={stickerImageSrc} />}
          <Box py="4x" textAlign="center" sx={{ flex: '2 1 100%' }}>
            <Heading size="300" m="0x">
              {children}
            </Heading>
            {subtitle && (
              <Text size="50" textColor="secondary" mt="1x">
                {subtitle}
              </Text>
            )}
          </Box>
          {stickerAlignment === 'right' && <QuickFactSticker stickerImageSrc={stickerImageSrc} />}
        </Flex>
      </Section>
    </ThemeColorContext.Provider>
  );
};

export const QuickFactHighlight: FC<TextProps> = (props) => {
  const { colors } = useTheme();
  const themeColor = useContext(ThemeColorContext);

  return (
    <Text
      size="300"
      fontWeight="semibold"
      px="1x"
      sx={{ position: 'relative', whiteSpace: 'nowrap', zIndex: '1' }}
    >
      {props.children}
      <Box
        position="absolute"
        height="0.75em"
        width="100%"
        top="50%"
        left="0"
        borderRadius="round"
        background={colors[themeColor]['200']}
        zIndex="-1"
        sx={{ transform: 'translateY(-50%)' }}
      />
    </Text>
  );
};

export default QuickFact;
